import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Toggle from 'components/toggle';
import Button from 'shared/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation, faClose, faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { put } from 'helpers/api';
import { Banner } from '@buildoutinc/blueprint-react/ui/Banner';
import { showSnackbar } from 'components/ui/snackbar/helper';

export default function LimitBrokersOnPlugin({ defaultLimit, limit: initialLimit, url }) {
  const [startingLimit, setStartingLimit] = useState(initialLimit);
  const [limit, setLimit] = useState(initialLimit);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState([]);

  function toggleLimit() {
    if (limit === null) {
      setLimit(defaultLimit);
    } else {
      setLimit(null);
    }
  }

  async function saveChanges() {
    setIsSaving(true);
    setErrors([]);
    const payload = { plugins_plugin_set: { broker_limit: limit } };
    try {
      await put(url, payload);
      setStartingLimit(limit);
      showSnackbar({ text: 'Updated Broker limit for plugin', variant: 'success' });
    } catch (error) {
      setErrors(error);
    } finally {
      setIsSaving(false);
    }
  }

  const tooltip = (
    <Tooltip id="plugin-default-tooltip">
      Turning this option on will divide the brokers into multiple pages,
      letting users navigate through data in smaller, manageable chunks.
    </Tooltip>
  );

  return (
    <div className="d-flex flex-column gap-3 mb-5">
      <p className="fs-large m-0 d-inline-flex gap-2 align-items-center">
        Limit Brokers on Plugin
        <OverlayTrigger overlay={tooltip} placement="bottom">
          <FontAwesomeIcon icon={faQuestionCircle}/>
        </OverlayTrigger>
      </p>
      {errors.length > 0 && (
        <Banner className="align-items-center" variant="destructive">
          <Banner.Content>
            <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
            <div className="flex-fill">
              <div><b>The following errors occurred when trying to save. Please fix and re-submit.</b></div>
              <div>{errors.join('\n')}</div>
            </div>
          </Banner.Content>
          <Banner.Actions>
            <Button icon={faClose} outlined onClick={() => setErrors([])} />
          </Banner.Actions>
        </Banner>
      )}
      <div className="d-flex align-items-center gap-3">
        <Toggle checked={limit !== null} showText={false} theme="ds" onChange={toggleLimit}/>
        <span>Limit the amount of brokers that appear on your plugin per page</span>
      </div>

      {limit !== null && (
        <div>
          <label>Broker Limit Per Page</label>
          <input
            className="form-control w-25"
            type="number"
            value={limit}
            onChange={e => setLimit(e.currentTarget.value)}
          />
        </div>
      )}
      {startingLimit !== limit && (
        <div>
          <Button.Primary disabled={isSaving} onClick={saveChanges}>Save</Button.Primary>
        </div>
      )}
    </div>
  );
}

LimitBrokersOnPlugin.propTypes = {
  defaultLimit: PropTypes.number.isRequired,
  limit: PropTypes.number,
  url: PropTypes.string.isRequired,
};
